import { cn } from "@bcc/lib/utils";
import type { DataTableRow } from "@client-shared";
import type { ColumnDef, OnChangeFn, Row, RowSelectionState, VisibilityState } from "@tanstack/react-table";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { DataTableBulkActions } from "./data-table-bulk-actions";
import { DataTableEmptyState, DataTableLoadingRows } from "./data-table-empty-state";
import { hasHideableColumns } from "./data-table-column-visibility";
import { DataTableFilterBar } from "./data-table-filter-bar";
import { DataTableHeader } from "./data-table-header";
import { DataTablePagination } from "./data-table-pagination";
import { DataTableRowComponent } from "./data-table-row";
import type { DataTableFilterSelection, DataTableProps, GroupedRowActionHelpers } from "./data-table.types";
import { setDataTableContainerTestId } from "./data-table.utils";
import { useDataTable } from "./use-data-table";
import { useDataTablePersistence } from "./use-data-table-persistence";

/**
 * Drops persisted entries for columns that can no longer be hidden.
 *
 * A column marked `enableHiding: false` in a later release, after users had already hidden it,
 * would otherwise stay hidden while being absent from the Columns menu — no way to bring it back,
 * and no menu at all if every remaining column is locked.
 */
const withoutLockedColumns = <T extends object>(
  stored: VisibilityState | undefined,
  columns: ColumnDef<DataTableRow<T>>[]
): VisibilityState | undefined => {
  if (stored === undefined) return undefined;

  const locked = columns.reduce<Set<string>>((lockedIds, column) => {
    // TanStack resolves a column's id from `id`, falling back to `accessorKey`.
    const definition = column as { id?: string; accessorKey?: string };
    const columnId = definition.id ?? definition.accessorKey;
    if (column.enableHiding === false && columnId !== undefined) lockedIds.add(columnId);
    return lockedIds;
  }, new Set());
  if (locked.size === 0) return stored;

  return Object.entries(stored).reduce<VisibilityState>((visibility, [columnId, isVisible]) => {
    if (!locked.has(columnId)) visibility[columnId] = isVisible;
    return visibility;
  }, {});
};

// Stable empty arrays to prevent TanStack Table cache invalidation on every render
const EMPTY_BULK_ACTIONS: never[] = [];
const EMPTY_PROMOTED_ACTIONS: never[] = [];
const EMPTY_GROUPING: string[] = [];

/**
 * DataTable Component
 *
 * A full-featured data table built on TanStack Table with:
 * - Sorting (click column headers)
 * - Filtering (global search + column filters)
 * - Pagination (with keyboard navigation)
 * - Row selection (with bulk actions)
 * - Row tones (success, warning, critical, subdued, canceled)
 * - CSV export
 * - Loading/error/empty states
 *
 * @example
 * ```tsx
 * <DataTable
 *   data={products}
 *   columns={columns}
 *   selectable
 *   searchable
 *   exportable
 *   bulkActions={[
 *     { label: 'Delete', onClick: handleDelete }
 *   ]}
 * />
 * ```
 */
export function DataTable<T extends object>({
  data,
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  enableColumnVisibility = false,
  getColumnLabel,

  // Persistence
  persistenceKey,
  persistColumnVisibility = true,
  persistFilters = true,

  // Selection
  selectable = false,
  maxSelectableRows,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,

  // Sorting
  sorting: controlledSorting,
  onSortingChange,

  // Filtering
  searchable = false,
  searchPlaceholder,
  filters,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange,
  globalFilter: controlledGlobalFilter,
  globalFilterFn,

  // Pagination
  pagination: controlledPagination,
  onPaginationChange,
  pageSizeOptions,
  totalRows,

  // Actions
  bulkActions = EMPTY_BULK_ACTIONS,
  promotedActions = EMPTY_PROMOTED_ACTIONS,

  // Export
  exportable = false,
  exportFilename,
  exportColumns,
  onExportRows,

  // States
  isLoading = false,
  isError = false,
  errorMessage,
  emptyMessage,

  // Grouping
  grouping = EMPTY_GROUPING,
  showGroupCount = false,
  renderGroupSummary,
  groupedRowActions,
  // Expansion
  getRowCanExpand,

  // Styling
  className,

  // Callbacks
  onRowClick,

  // i18n - will be overridden with translations
  noResultsText: noResultsTextProp,
  errorText: errorTextProp,
  exportText: exportTextProp,
  clearText: clearTextProp,
  searchBadgePrefix: searchBadgePrefixProp,
  exportToCSV,
  enableGroupExpand,
}: DataTableProps<T>) {
  const { t } = useTranslation();

  // Use translations with prop overrides
  const noResultsText = noResultsTextProp ?? t("dataTable.noResults");
  const errorText = errorTextProp ?? t("dataTable.error");
  const exportText = exportTextProp ?? (t("dataTable.export") as string);
  const clearText = clearTextProp ?? t("dataTable.clear");
  const searchBadgePrefix = searchBadgePrefixProp ?? t("dataTable.searchBadge");

  // Internal selection state for uncontrolled mode
  const [internalRowSelection, setInternalRowSelection] = React.useState<RowSelectionState>({});
  const effectiveRowSelection = controlledRowSelection ?? internalRowSelection;
  // Track selection count for maxSelectableRows logic
  const currentSelectionCount = Object.keys(effectiveRowSelection).length;

  // Memoize the row selection change handler to prevent creating new references each render
  const handleRowSelectionChange = React.useCallback(
    (updater: Parameters<OnChangeFn<RowSelectionState>>[0]) => {
      if (onRowSelectionChange) {
        onRowSelectionChange(updater);
      } else {
        setInternalRowSelection((prev) => (typeof updater === "function" ? updater(prev) : updater));
      }
    },
    [onRowSelectionChange]
  );

  // Memoize enableRowSelection to prevent new function reference each render
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- manual memoization preserved until React Compiler is enabled across the workspace
  const enableRowSelectionValue = React.useMemo(() => {
    if (!selectable) return false;
    if (!maxSelectableRows) {
      return (row: Row<DataTableRow<T>>) => !row.original.isDisabled;
    }
    return (row: Row<DataTableRow<T>>) => {
      if (row.original.isDisabled) return false;
      if (row.getIsGrouped()) return true;
      const isSelected = effectiveRowSelection[row.id] === true;
      return isSelected || currentSelectionCount < maxSelectableRows;
    };
  }, [selectable, maxSelectableRows, effectiveRowSelection, currentSelectionCount]);

  const persistence = useDataTablePersistence({ persistenceKey, persistColumnVisibility, persistFilters });

  // The persistence layer only owns visibility when the caller hasn't taken control of it.
  const managesColumnVisibility = persistenceKey !== undefined && persistColumnVisibility && onColumnVisibilityChange === undefined;
  const [persistedColumnVisibility, setPersistedColumnVisibility] = React.useState<VisibilityState>(
    () => withoutLockedColumns(persistence.restored.columnVisibility, columns) ?? columnVisibility ?? {}
  );

  // Mirrors the state so a functional updater resolves against the latest value rather than a
  // render-scoped closure — two visibility updates batched into one render would otherwise lose
  // the first. Kept in step by `applyColumnVisibility`, the only writer.
  const columnVisibilityRef = React.useRef(persistedColumnVisibility);
  const applyColumnVisibility = React.useCallback((next: VisibilityState) => {
    columnVisibilityRef.current = next;
    setPersistedColumnVisibility(next);
  }, []);

  const handlePersistedColumnVisibilityChange = React.useCallback<OnChangeFn<VisibilityState>>(
    (updaterOrValue) => {
      const next = typeof updaterOrValue === "function" ? updaterOrValue(columnVisibilityRef.current) : updaterOrValue;
      applyColumnVisibility(next);
      persistence.persist({ columnVisibility: next });
    },
    [applyColumnVisibility, persistence]
  );

  const handleActiveFiltersChange = React.useCallback(
    (selections: DataTableFilterSelection[]) => {
      // An empty selection has to be persisted explicitly when a chip is `defaultSelected`,
      // or the next mount restores that default and silently undoes the user's clear. With no
      // defaults, dropping the section is equivalent and leaves no stray entry behind.
      if (selections.length === 0 && !filters?.some((filter) => filter.defaultSelected)) {
        persistence.clear("filters");
        return;
      }
      persistence.persist({ filters: selections });
    },
    [filters, persistence]
  );

  // Initialize the table with the hook
  const { table, globalFilter, setGlobalFilter, rowSelection, setRowSelection } = useDataTable({
    data,
    columns,
    columnVisibility: managesColumnVisibility ? persistedColumnVisibility : columnVisibility,
    onColumnVisibilityChange: managesColumnVisibility ? handlePersistedColumnVisibilityChange : onColumnVisibilityChange,
    sorting: controlledSorting,
    onSortingChange,
    columnFilters: controlledColumnFilters,
    onColumnFiltersChange,
    globalFilter: controlledGlobalFilter,
    globalFilterFn,
    pagination: controlledPagination,
    onPaginationChange,
    rowSelection: effectiveRowSelection,
    onRowSelectionChange: handleRowSelectionChange,
    enableRowSelection: enableRowSelectionValue,
    grouping,
    getRowCanExpand,
  });

  const selectedCount = Object.values(effectiveRowSelection).filter(Boolean).length;
  const visibleColumns = table.getVisibleFlatColumns();
  const hasGrouping = grouping && grouping.length > 0;
  const showExpanderColumn = enableGroupExpand && hasGrouping;
  const colSpan = visibleColumns.length + (selectable ? 1 : 0) + (showExpanderColumn ? 1 : 0);
  const rows = table.getRowModel().rows;
  const hasFilters = searchable || (filters && filters.length > 0);
  const showColumnVisibility = enableColumnVisibility && hasHideableColumns(table);

  // Reset restores the caller's default map (all columns visible when none was given)
  // and drops the persisted override so a refresh doesn't bring the old state back.
  const handleResetColumnVisibility = React.useCallback(() => {
    const defaults = columnVisibility ?? {};
    // Always drop the persisted override, including when the caller controls visibility —
    // otherwise a later uncontrolled mount on the same key would restore the very columns this
    // just reset. No-ops when there is no key or the section is opted out.
    persistence.clear("columnVisibility");
    if (managesColumnVisibility) {
      applyColumnVisibility(defaults);
      return;
    }
    table.setColumnVisibility(defaults);
  }, [applyColumnVisibility, columnVisibility, managesColumnVisibility, persistence, table]);

  // Helpers object for groupedRowActions callbacks
  const helpers = React.useMemo<GroupedRowActionHelpers<T>>(
    () => ({ table, setRowSelection, maxSelectableRows }),
    [table, setRowSelection, maxSelectableRows]
  );

  // Clear selection handler
  const handleClearSelection = React.useCallback(() => {
    table.resetRowSelection();
  }, [table]);

  return (
    <div data-testid={setDataTableContainerTestId()} className={cn("rounded-lg border border-border bg-card overflow-hidden", className)}>
      {/* Filter bar */}
      {(hasFilters || exportable || promotedActions.length > 0 || showColumnVisibility) && (
        <DataTableFilterBar
          table={table}
          searchable={searchable}
          searchPlaceholder={searchPlaceholder}
          filters={filters}
          exportable={exportable}
          exportFilename={exportFilename}
          exportColumns={exportColumns}
          onExportRows={onExportRows}
          promotedActions={promotedActions}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          exportText={exportText}
          clearText={clearText}
          searchBadgePrefix={searchBadgePrefix}
          exportToCSV={exportToCSV}
          initialActiveFilters={persistence.restored.filters}
          onActiveFiltersChange={handleActiveFiltersChange}
          showColumnVisibility={showColumnVisibility}
          onResetColumnVisibility={handleResetColumnVisibility}
          getColumnLabel={getColumnLabel}
        />
      )}

      {/* Bulk actions */}
      {selectable && bulkActions.length > 0 && (
        <DataTableBulkActions
          selectedCount={selectedCount}
          bulkActions={bulkActions}
          rowSelection={rowSelection}
          onClearSelection={handleClearSelection}
          table={table}
          setRowSelection={setRowSelection}
          maxSelectableRows={maxSelectableRows}
        />
      )}

      {/* Table */}
      <div className="relative overflow-x-auto">
        <table className="w-full">
          <DataTableHeader
            table={table}
            selectable={selectable}
            showExpanderColumn={showExpanderColumn}
            maxSelectableRows={maxSelectableRows}
            setRowSelection={setRowSelection}
          />

          <tbody>
            {/* Loading state */}
            {isLoading && (
              <DataTableLoadingRows colSpan={visibleColumns.length} hasSelection={selectable} hasExpander={showExpanderColumn} />
            )}

            {/* Error state */}
            {!isLoading && isError && <DataTableEmptyState colSpan={colSpan} type="error" message={errorMessage || errorText} />}

            {/* Empty state */}
            {!isLoading && !isError && rows.length === 0 && (
              <DataTableEmptyState colSpan={colSpan} type="empty" message={emptyMessage || noResultsText} />
            )}

            {/* Data rows */}
            {!isLoading &&
              !isError &&
              rows.map((row) => (
                <DataTableRowComponent
                  key={row.id}
                  row={row}
                  selectable={selectable}
                  onClick={onRowClick}
                  showExpanderColumn={showExpanderColumn}
                  showGroupCount={showGroupCount}
                  renderGroupSummary={renderGroupSummary}
                  rowSelection={rowSelection}
                  groupedRowActions={groupedRowActions}
                  helpers={helpers}
                />
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && !isError && rows.length > 0 && (
        <DataTablePagination table={table} pageSizeOptions={pageSizeOptions} totalRows={totalRows} showSelectedCount={selectable} />
      )}
    </div>
  );
}
